import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import './StripeInlineCheckout.css';

// Cache stripe promises by publishable key to avoid reloading Stripe.js
const stripePromiseCache = new Map();

// Inner component that uses Stripe hooks
const CheckoutForm = ({
  amount,
  announcementId,
  centerId,
  onSuccess,
  onError,
  onCancel,
  useQuickCheckout = false
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const handleConfirm = async () => {
    if (!stripe || !elements) {
      console.debug('[Stripe] Confirm called before hooks ready');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.debug('[Stripe] Submitting elements before confirm');
      const { error: submitError } = await elements.submit();
      if (submitError) throw submitError;

      console.debug('[Stripe] Calling confirmPayment with redirect if required');
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?announcement=${announcementId}`,
        },
        redirect: 'if_required'
      });

      if (confirmError) throw confirmError;

      console.debug('[Stripe] Payment confirmed successfully');
      onSuccess();
    } catch (err) {
      console.error('Payment error:', err);
      const msg = err?.message || 'Payment failed';
      setError(msg);
      onError(msg);
    } finally {
      setLoading(false);
    }
  };

  const expressCheckoutOptions = {
    onConfirm: handleConfirm,
    onCancel: () => {
      console.log('Payment cancelled');
      onCancel();
    }
  };

  return (
    <div className="stripe-checkout-container">
      <div className="payment-header">
        <h4>Complete Your Sponsorship</h4>
        <p className="payment-amount">Amount: ${amount}</p>
      </div>

      {error && (
        <div className="payment-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div className="express-checkout-wrapper">
        <ExpressCheckoutElement options={expressCheckoutOptions} />
      </div>

      <div className="payment-divider">
        <span>or pay with card</span>
      </div>

      <div className="payment-element-wrapper">
        <PaymentElement />
      </div>

      <button
        className="manual-pay-button"
        onClick={handleConfirm}
        disabled={loading || !stripe || !elements}
      >
        {loading ? 'Processing...' : `Pay $${amount}`}
      </button>

      <button
        className="cancel-button"
        onClick={onCancel}
        disabled={loading}
      >
        Cancel
      </button>

      <div className="payment-footer">
        <p className="security-note">
          Payments are secure and encrypted
        </p>
      </div>
    </div>
  );
};

// Main component that provides Stripe context
const StripeInlineCheckout = ({
  amount,
  announcementId,
  centerId,
  publishableKey,
  testMode,
  onSuccess,
  onError,
  onCancel
}) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [initError, setInitError] = useState(null);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    if (publishableKey) {
      if (!stripePromiseCache.has(publishableKey)) {
        console.debug('[Stripe] Creating new stripePromise for key prefix:', publishableKey.slice(0, 7));
        stripePromiseCache.set(publishableKey, loadStripe(publishableKey));
      }
      setStripePromise(stripePromiseCache.get(publishableKey));
    }
  }, [publishableKey]);

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        setInitializing(true);
        setInitError(null);

        console.debug('[Stripe] Creating PaymentIntent', { amount, announcementId, centerId });
        const response = await fetch('/.netlify/functions/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount, announcementId, centerId, currency: 'usd' })
        });

        const data = await response.json();
        if (!response.ok) {
          console.error('[Stripe] Create PI failed', { status: response.status, data });
          throw new Error(data.error || 'Failed to create payment intent');
        }

        console.debug('[Stripe] Received client secret (truncated)');
        setClientSecret(data.clientSecret);
      } catch (err) {
        console.error('Error creating payment intent:', err);
        const msg = err?.message || 'Failed to create payment intent';
        setInitError(msg);
        if (onError) onError(msg);
      } finally {
        setInitializing(false);
      }
    };

    if (amount && announcementId && centerId) {
      createPaymentIntent();
    }
  }, [amount, announcementId, centerId, onError]);

  if (!publishableKey) {
    return (
      <div className="stripe-checkout-container">
        <div className="payment-error">
          <span className="error-icon">⚠️</span>
          Payment system is not configured. Please contact support.
        </div>
      </div>
    );
  }

  if (!stripePromise || initializing) {
    return (
      <div className="stripe-checkout-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading payment system...</p>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="stripe-checkout-container">
        <div className="payment-error">
          <span className="error-icon">⚠️</span>
          {initError}
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="stripe-checkout-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Preparing payment...</p>
        </div>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: { colorPrimary: '#667eea' },
        },
      }}
    >
      <CheckoutForm
        amount={amount}
        announcementId={announcementId}
        centerId={centerId}
        onSuccess={onSuccess}
        onError={onError}
        onCancel={onCancel}
      />
    </Elements>
  );
};

export default StripeInlineCheckout;